import { sortBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDataMutation, useDataProvider } from '@work4all/data';

import { Salutation } from '@work4all/models/lib/Classes/Salutation.entity';
import { DataRequest, SortDirection } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EditTableColumns } from '../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/types';
import { EditableEntityTable } from '../editable-entity-table/EditableEntityTable';

const SALUTATION_DATA: Salutation = {
  id: null,
  name: null,
  number: null,
  isFemale: null,
  isMale: null,
  nameOrFirstName: null,
  standardletterSalutation: null,
  title: null,
};

const newEntryMandatoryFields: (keyof Salutation)[] = ['name'];

const req: DataRequest = {
  data: SALUTATION_DATA,
  entity: Entities.salutation,
  completeDataResponse: true,
  sort: [
    {
      direction: SortDirection.ASCENDING,
      field: 'number',
    },
  ],
};

function move(input: Salutation[], from: number, to: number) {
  const elm = input.splice(from, 1)[0];
  input.splice(to, 0, elm);
}

export const SalutationTable = () => {
  const { t } = useTranslation();

  const cols = useMemo<EditTableColumns<Salutation>[]>(
    () => [
      {
        Header: t('COMMON.NAME'),
        accessor: 'name',
        width: 150,
        type: 'text',
      },
      {
        Header: t('COMMON.TITLE'),
        accessor: 'title',
        width: 150,
        type: 'text',
      },
      {
        Header: t('COMMON.STANDARD_LETTER_SALUTATION'),
        accessor: 'standardletterSalutation',
        width: 300,
        type: 'text',
      },
      {
        Header: t('COMMON.NAME_OR_FIRST_NAME'),
        accessor: 'nameOrFirstName',
        width: 150,
        type: 'checkbox',
      },
      {
        Header: t('COMMON.FEMALE_DEF'),
        accessor: 'isFemale',
        width: 150,
        type: 'checkbox',
      },
      {
        Header: t('COMMON.MALE_DEF'),
        accessor: 'isMale',
        width: 150,
        type: 'checkbox',
      },
    ],
    [t]
  );

  const [mutate] = useDataMutation({
    entity: Entities.salutation,
    mutationType: EMode.upsert,
    responseData: { id: null, number: null } as Salutation,
  });

  const { data } = useDataProvider<Salutation>(req);
  const sortedData = useMemo(() => sortBy(data, 'number'), [data]);
  const handleMovePosition = useCallback(
    (localId: number, newIdx: number) => {
      const newOrder = [...sortedData];
      const oldIdx = sortedData.findIndex((x) => x.id === localId);

      move(newOrder, oldIdx, newIdx);

      newOrder.forEach((item, idx) => {
        mutate({ id: item.id, number: idx });
      });
    },
    [mutate, sortedData]
  );

  const handleTransformBeforeUpdate = useCallback(
    (salutation: Salutation) => {
      if (salutation.id === undefined && salutation.number === undefined) {
        salutation.number = sortedData.length;
      }
      return salutation;
    },
    [sortedData.length]
  );

  return (
    <EditableEntityTable<Salutation>
      columns={cols}
      dataFields={SALUTATION_DATA}
      entity={Entities.salutation}
      idType="number"
      sortByField="number"
      newEntryMandatoryFields={newEntryMandatoryFields}
      onMovePosition={handleMovePosition}
      transformBeforeUpdate={handleTransformBeforeUpdate}
      enableDelete
      dragHandlerVisible={(val) => {
        return val.id !== -1;
      }}
      dragHandlerClickable={() => false}
      dataProviderProps={{
        completeDataResponse: true,
      }}
    />
  );
};
